import { Plus } from 'lucide-react';
import { useState, useMemo, useRef } from 'react';
import { ListLevelLink } from './-LevelLink';
import { getRouteApi, useParams } from '@tanstack/react-router';
import { Tag } from '~/util/types/tags';
import { NlwTier, nlwTiers, nlwTierColorClasses } from '~/util/types/nlw';
import { useList } from '~/util/hooks/useList';
import { LevelStatus, LevelWithExtraFilters } from '~/util/types/levels';
import { ScrollArea } from '~/primitives/scroll-area';
import { rangeProperties } from './-SearchParams';
import { useQuery } from '@tanstack/react-query';
import { idlQuery } from '~/util/queries/public/other';
import { NewLevelSheet } from './-NewLevelSheet';
import { cn, primaryClickHandler } from '~/util';
import { useScopes } from '~/components/Providers/AuthProvider';
import { recordLevelStatsQuery } from '~/util/queries/staff/stats';
import { useFavoriteLevelsStore } from '~/util/stores/favoriteLevels';
import { Separator } from '~/primitives/separator';
import { useAutoScrollIntoView } from '~/util/hooks/useAutoScrollIntoView';
import { useElementMap } from '~/util/hooks/useElementMap';
import { levelsQuery } from '~/util/queries/public/levels';
import { useHydratedQuery } from '~/util/hooks/useHydratedQuery';
import Load from '~/components/Load';

const Route = getRouteApi('/list');

type LevelWithExtraFiltersAndIDL = LevelWithExtraFilters & {
    idl_position: number;
};

type ListEntry =
    | {
          type: 'header';
          label: NlwTier | LevelStatus;
      }
    | {
          type: 'level';
          level: LevelWithExtraFiltersAndIDL;
      };

function LevelsListData({ onLevelSelect }: { onLevelSelect?: () => void }) {
    const list = useList();
    const { search, filters, sort, reverse, time_machine, tiers_mode } =
        Route.useSearch({
            select: ({
                search,
                filters,
                sort,
                reverse,
                time_machine,
                tiers_mode,
            }) => ({
                search,
                filters,
                sort,
                reverse,
                time_machine,
                tiers_mode,
            }),
        });

    const { data = [], isLoading } = useHydratedQuery(
        levelsQuery({ list, at: time_machine }),
    );

    const { data: idl } = useQuery(idlQuery());
    const { hasScope } = useScopes();

    const recordLevelStats = useQuery(
        recordLevelStatsQuery({ list }),
    ).data?.reduce(
        (acc, { level, records }) => {
            if (level) acc[level.id] = records;
            return acc;
        },
        {} as Record<string, number>,
    );

    const { favorite_levels, addFavoriteLevel, removeFavoriteLevel } =
        useFavoriteLevelsStore();

    const favorite_levels_set = useMemo(
        () => new Set(favorite_levels),
        [favorite_levels],
    );

    const viewportRef = useRef<HTMLDivElement>(null);

    const show1987EasterEgg = time_machine
        ? new Date(time_machine)?.getFullYear() === 1987
        : false;

    const { levelid } = useParams({ strict: false });

    const isTwoPlayer = levelid?.endsWith('_2p') || false;
    const baseLevelId = levelid?.replace('_2p', '');

    const list_data = useMemo(
        () =>
            data?.map((level) => ({
                ...level,
                records: recordLevelStats?.[level.id],
                is_favorite: favorite_levels_set.has(level.id),
            })) ?? ([] as LevelWithExtraFilters[]),
        [data, recordLevelStats, favorite_levels_set],
    );

    const list_with_idl_data = useMemo(() => {
        return list_data.map((level) => ({
            ...level,
            idl_position:
                level.status == 'Legacy'
                    ? (idl?.find((levelIDL) => levelIDL.name === level.name)
                          ?.position ?? -1)
                    : -1,
        }));
    }, [list_data, idl]);

    // Filtering and sorting
    const filteredList = useMemo(() => {
        const searchAsNumber = search !== '' ? Number(search) : NaN;
        const selectedTiers = new Set(filters.tiers);

        return list_with_idl_data.filter(
            (level) =>
                ((!isNaN(searchAsNumber) &&
                    (level.level_id === searchAsNumber ||
                        level.position === searchAsNumber ||
                        level.song === searchAsNumber)) ||
                    level.name.toLowerCase().includes(search.toLowerCase())) &&
                Object.keys(filters.tags).every(
                    (tag) =>
                        level.tags &&
                        level.tags.includes(tag as Tag) ===
                            filters.tags[tag as Tag],
                ) &&
                rangeProperties.every((property) => {
                    const value = level[property];
                    const range = filters.ranges[property];
                    if (range.min === undefined && range.max === undefined)
                        return true;
                    if (value == null) return false;
                    return (
                        (range.min !== undefined
                            ? Number(value) >= range.min
                            : true) &&
                        (range.max !== undefined
                            ? Number(value) <= range.max
                            : true)
                    );
                }) &&
                (filters.tiers.length === 0 ||
                    selectedTiers.has((level.nlw_tier ?? '') as NlwTier)) &&
                (filters.completed_by_user === 'all' ||
                    (filters.completed_by_user === 'completed'
                        ? level.completed_by_user === true
                        : level.completed_by_user === false)) &&
                (!filters.favorited_by_user || level.is_favorite),
        );
    }, [
        list_with_idl_data,
        search,
        filters.tags,
        filters.ranges,
        filters.tiers,
        filters.completed_by_user,
        filters.favorited_by_user,
    ]);

    const sortedFilteredList = useMemo(() => {
        return [...filteredList].sort((levelA, levelB) => {
            let result = 0;

            if (sort === 'rank') {
                const isPlacedA = levelA.position !== null;
                const isPlacedB = levelB.position !== null;

                if (isPlacedA != isPlacedB) {
                    // first sort all placed levels (MainList/Legacy) above unplaced ones (Pending/Removed)
                    result = isPlacedA ? -1 : 1;
                } else if (!isPlacedA && !isPlacedB) {
                    // for unplaced ones sort by name
                    result = levelA.name
                        .toLowerCase()
                        .localeCompare(levelB.name.toLowerCase());
                } else {
                    // then sort placed ones by position, MainList above Legacy
                    const isOnIDLA = levelA.idl_position !== -1;
                    const isOnIDLB = levelB.idl_position !== -1;

                    if (
                        // for legacy levels, sort by IDL pos if available or by regular pos otherwise
                        levelA.status == 'Legacy' &&
                        levelB.status == 'Legacy' &&
                        (isOnIDLA || isOnIDLB)
                    ) {
                        if (isOnIDLA != isOnIDLB) {
                            result = isOnIDLA ? -1 : 1;
                        } else if (isOnIDLA && isOnIDLB) {
                            result = levelA.idl_position - levelB.idl_position;
                        } else {
                            result = levelA.position! - levelB.position!;
                        }
                    } else {
                        result = levelA.position! - levelB.position!;
                    }
                }
            } else if (sort === 'enjoyment') {
                // for enjoyment, sort levels by edel value and those without one below
                const isOnEDELA = !!levelA.edel_enjoyment;
                const isOnEDELB = !!levelB.edel_enjoyment;

                if (isOnEDELA !== isOnEDELB) {
                    result = isOnEDELA ? -1 : 1;
                } else if (isOnEDELA && isOnEDELB) {
                    result = levelB.edel_enjoyment! - levelA.edel_enjoyment!;
                }
            } else if (sort === 'records') {
                result = (levelB.records ?? 0) - (levelA.records ?? 0);
            } else if (sort === 'creation_date') {
                result = levelB.level_id - levelA.level_id;
            }
            return reverse ? -result : result;
        });
    }, [filteredList, sort, reverse]);

    // add headers (nlw view tiers, legacy etc)

    const listEntries = useMemo<ListEntry[]>(() => {
        const entries: ListEntry[] = [];
        const levelEntriesByStatus = sortedFilteredList.reduce(
            (acc, level) => {
                const statusEntries = acc[level.status] ?? [];
                statusEntries.push({
                    type: 'level',
                    level,
                });
                acc[level.status] = statusEntries;

                if (level.status != 'Removed') {
                    const allButRemovedEntries = acc['AllButRemoved'] ?? [];
                    allButRemovedEntries.push({
                        type: 'level',
                        level,
                    });
                    acc['AllButRemoved'] = allButRemovedEntries;
                }
                return acc;
            },
            {} as Record<LevelStatus | 'AllButRemoved', ListEntry[]>,
        );

        if (!tiers_mode) {
            if (sort == 'rank') {
                // rank sort mode shows legacy and pending separately; other sorts can mix them
                entries.push(...(levelEntriesByStatus['MainList'] ?? []));
                if (levelEntriesByStatus['Legacy']?.length > 0)
                    entries.push(
                        { type: 'header', label: 'Legacy' },
                        ...levelEntriesByStatus['Legacy'],
                    );
                if (levelEntriesByStatus['Pending']?.length > 0)
                    entries.push(
                        { type: 'header', label: 'Pending' },
                        ...levelEntriesByStatus['Pending'],
                    );
            } else
                entries.push(...(levelEntriesByStatus['AllButRemoved'] ?? []));
        } else {
            const levelsByTier = sortedFilteredList
                .filter((level) => level.status != 'Removed')
                .reduce(
                    (acc, level) => {
                        const tier = level.nlw_tier as NlwTier;
                        const tierLevels = acc[tier] ?? [];
                        tierLevels.push(level);
                        acc[tier] = tierLevels;
                        return acc;
                    },
                    {} as Record<NlwTier, LevelWithExtraFiltersAndIDL[]>,
                );

            entries.push(
                ...nlwTiers.flatMap((tier) => {
                    const tierLevels = levelsByTier[tier] ?? [];
                    if (tierLevels.length === 0) return [] as ListEntry[];

                    return [
                        { type: 'header', label: tier },
                        ...tierLevels.map((level) => ({
                            type: 'level' as const,
                            level,
                        })),
                    ] as ListEntry[];
                }),
            );
        }

        if (
            hasScope('level_modify') &&
            levelEntriesByStatus['Removed']?.length > 0
        ) {
            entries.push(
                { type: 'header', label: 'Removed' },
                ...levelEntriesByStatus['Removed'],
            );
        }

        return entries;
    }, [sortedFilteredList, tiers_mode, sort, hasScope]);

    // Autoscroll to selected level

    const {
        getElement: getLevelElement,
        registerElement: registerLevelElement,
    } = useElementMap<string, HTMLAnchorElement>();

    const selectedLevel = useMemo(() => {
        if (!baseLevelId) return null;

        return (
            sortedFilteredList.find(
                (level) =>
                    level.id === baseLevelId ||
                    (level.level_id.toString() === baseLevelId &&
                        level.two_player === isTwoPlayer),
            ) ?? null
        );
    }, [sortedFilteredList, baseLevelId, isTwoPlayer]);

    useAutoScrollIntoView({
        viewportRef,
        enabled: selectedLevel != null,
        scrollKey: `${search}|${selectedLevel?.id ?? ''}`,
        getTarget: () =>
            selectedLevel ? getLevelElement(selectedLevel.id) : null,
    });

    return (
        <ScrollArea
            className="h-[calc(100vh-305px)] w-full"
            viewportRef={viewportRef}
        >
            <div>
                {hasScope('level_modify') && <NewLevelButton />}
                {isLoading && <Load />}
                {listEntries.map((entry) => {
                    if (entry.type === 'header') {
                        return (
                            <div key={`label-${entry.label}`} className="py-2">
                                <div
                                    className={cn(
                                        'flex items-center gap-3',
                                        entry.label in nlwTierColorClasses
                                            ? nlwTierColorClasses[
                                                  entry.label as NlwTier
                                              ].header
                                            : 'text-gray-400',
                                    )}
                                >
                                    <Separator className="flex-1 bg-current h-1" />
                                    <span className="font-semibold uppercase text-center text-inherit">
                                        {entry.label in nlwTiers
                                            ? `${entry.label} Tier`
                                            : entry.label}
                                    </span>
                                    <Separator className="flex-1 bg-current h-1" />
                                </div>
                            </div>
                        );
                    }

                    const { level } = entry;
                    return (
                        <ListLevelLink
                            ref={registerLevelElement(level.id)}
                            key={level.id}
                            level={level}
                            showNlwBorder={tiers_mode}
                            idlpos={level.idl_position}
                            onClick={primaryClickHandler(onLevelSelect)}
                            editFavorite={(favorite) => {
                                if (favorite) addFavoriteLevel(level.id);
                                else removeFavoriteLevel(level.id);
                            }}
                        />
                    );
                })}
                {show1987EasterEgg && (
                    <img
                        src="/assets/1987.webp"
                        alt="1987 easter egg"
                        className="object-contain drop-shadow-2xl"
                    />
                )}
            </div>
        </ScrollArea>
    );
}

export default LevelsListData;

function NewLevelButton() {
    const [newLevelSheetOpen, setNewLevelSheetOpen] = useState(false);
    return (
        <>
            <button
                onClick={() => setNewLevelSheetOpen(true)}
                className={cn(
                    'p-2 rounded-md my-1 mb-4',
                    'flex w-full gap-x-2 items-center transition-colors cursor-pointer',
                    'bg-background-lighttransparent hover:bg-secondary-light',
                    'outline-dotted outline-neutral-700',
                )}
            >
                <Plus className="w-20 self-center" />
                <p className="text-start">Add level...</p>
            </button>
            <NewLevelSheet
                open={newLevelSheetOpen}
                onOpenChange={setNewLevelSheetOpen}
            />
        </>
    );
}
